/**
 * InsulaCharter - Sistema di Localizzazione (i18n)
 *
 * Utilizzo:
 * 1. Importa: import { t, setLocale, getLocale } from '@/locales';
 * 2. Usa: t('common.loading') => 'Caricamento...'
 * 3. Con variabili: t('dashboard.greeting', { name: 'Mario' }) => 'Bentornato, Mario...'
 * 4. Cambia lingua: setLocale('en')
 */

import it from './it';
import en from './en';
import de from './de';
import es from './es';
import pt from './pt';

type Locales = {
  [key: string]: typeof it;
};

// Lingue disponibili
export const locales: Locales = {
  it,
  en,
  de,
  es,
  pt,
};

// Lingua corrente (default: italiano)
let currentLocale = 'it';

/**
 * Imposta la lingua corrente
 * @param locale - Codice lingua (it, en, de)
 */
export const setLocale = (locale: string): void => {
  if (locales[locale]) {
    currentLocale = locale;
    // Salva preferenza nel localStorage
    if (typeof window !== 'undefined') {
      localStorage.setItem('insulacharter_locale', locale);
    }
  } else {
    console.warn(`Locale '${locale}' not found. Available: ${Object.keys(locales).join(', ')}`);
  }
};

/**
 * Ottiene la lingua corrente
 * @returns Codice lingua corrente
 */
export const getLocale = (): string => currentLocale;

/**
 * Ottiene tutte le lingue disponibili
 * @returns Array di codici lingua
 */
export const getAvailableLocales = (): string[] => Object.keys(locales);

/**
 * Funzione di traduzione
 * @param key - Chiave della stringa (es: 'common.loading')
 * @param vars - Variabili da sostituire (es: { name: 'Mario' })
 * @returns Stringa tradotta
 */
export const t = (key: string, vars: Record<string, string | number> = {}): string => {
  // Read locale from localStorage on every call to survive SSR hydration
  let activeLocale = currentLocale;
  if (typeof window !== 'undefined') {
    const saved = localStorage.getItem('insulacharter_locale');
    if (saved && locales[saved]) {
      activeLocale = saved;
    }
  }

  const keys = key.split('.');
  let value: unknown = locales[activeLocale];

  // Naviga nell'oggetto usando le chiavi
  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = (value as Record<string, unknown>)[k];
    } else {
      // Chiave non trovata, ritorna la chiave stessa
      console.warn(`Translation key not found: ${key}`);
      return key;
    }
  }

  // Se il valore non è una stringa, ritorna la chiave
  if (typeof value !== 'string') {
    console.warn(`Translation key '${key}' is not a string`);
    return key;
  }

  // Sostituisci le variabili {varName} con i valori
  return value.replace(/\{(\w+)\}/g, (match, varName) => {
    return vars[varName] !== undefined ? String(vars[varName]) : match;
  });
};

// Inizializza la lingua dal localStorage (se disponibile)
if (typeof window !== 'undefined') {
  const savedLocale = localStorage.getItem('insulacharter_locale');
  if (savedLocale && locales[savedLocale]) {
    currentLocale = savedLocale;
  }
}

export default { t, setLocale, getLocale, getAvailableLocales, locales };
